/**
 * Raster chart layer — shows a georeferenced PMTiles raster (a scanned paper
 * chart) beneath the chart overlays. The PMTiles archive is served as a static
 * file and read in the browser through the pmtiles protocol, so only the tiles
 * in view are fetched. The raster layer is inserted at the bottom of the stack,
 * so own ship, routes, tidal arrows and marks all draw on top of it.
 */
import maplibregl, { type Map as MaplibreMap } from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import { Capacitor } from '@capacitor/core';
import type { RasterSource } from '@/chart-sources/providers';

let registered = false;
/** Register the pmtiles:// protocol once, so raster sources can read archives. */
export function registerPmtiles(): void {
  if (registered) return;
  const protocol = new Protocol();
  maplibregl.addProtocol('pmtiles', protocol.tile);
  registered = true;
}

/**
 * One source and one layer PER CHART, so a set of adjacent charts draws as a quilt
 * rather than one-at-a-time. Ids are `raster-chart-<chartId>`; the bare
 * `raster-chart` is the id the old single-chart layer used and is still recognised
 * as ours so an older style gets cleaned up.
 */
const SRC_PREFIX = 'raster-chart-src-';
const LYR_PREFIX = 'raster-chart-';
const LEGACY_ID = 'raster-chart';

/** Is this layer id one of ours (current scheme or the old single-chart one)? */
function isRasterChartLayer(id: string): boolean {
  return id === LEGACY_ID || id.startsWith(LYR_PREFIX);
}

/**
 * Build the pmtiles:// URL for a chart, by where its archive lives:
 *   - 'remote': `path` is already an absolute URL, streamed via HTTP range.
 *   - 'local':  `path` is a device file URI; convertFileSrc makes it WebView-
 *     readable (a capacitor/localhost URL the range reader can seek in).
 *   - 'bundled' (default): `path` is relative to the app base URL.
 */
function pmtilesUrl(path: string, source: RasterSource = 'bundled'): string {
  if (source === 'remote') return `pmtiles://${path}`;
  if (source === 'local') return `pmtiles://${Capacitor.convertFileSrc(path)}`;
  const base = import.meta.env.BASE_URL ?? '/';
  return `pmtiles://${new URL(base + path, window.location.href).href}`;
}

/**
 * Where the quilt sits: directly ABOVE the basemaps and BELOW everything else.
 *
 * This used to be "the lowest layer that isn't the background", which was right
 * until the rough fallback basemap arrived. That group starts with `fbbase-water`,
 * a BACKGROUND layer that paints the whole viewport, so inserting the chart below
 * it hid every raster chart behind a flat blue rectangle. That is why the Antares
 * charts stopped showing. The basemaps (fallback locator, OpenSeaMap vector base,
 * satellite imagery) are all base, so we skip past them and sit on top.
 */
function bottomBeforeId(map: MaplibreMap): string | undefined {
  const layers = map.getStyle()?.layers ?? [];
  for (const l of layers) {
    if (l.id === 'background') continue;
    if (l.id === 'tile-basemap') continue;
    if (l.id.startsWith('fbbase-')) continue;
    if (l.id.startsWith('mtbase-')) continue;
    if (isRasterChartLayer(l.id)) continue;
    return l.id;
  }
  return undefined;
}

export interface ActiveRasterChart {
  id: string;
  path: string;
  /** Where the PMTiles lives (bundled / remote / local); defaults to bundled. */
  source?: RasterSource;
  minZoom: number;
  maxZoom: number;
  bounds: [number, number, number, number];
  /** Provider/chart attribution shown on the map (e.g. the RYA or BSB notice). */
  attribution?: string;
}

/**
 * Show a QUILT of raster charts, bottom of the list first.
 *
 * A provider is a SET of charts (the RYA scans, 823 Antares plans), and a set only
 * behaves as one seamless source if we lay several down at once: the small-scale
 * chart underneath for the wide picture, the detailed plans on top where they
 * exist. Showing one at a time meant everything outside that one chart's bounds had
 * no chart at all, which is what the Antares set looked like off Mull.
 *
 * The charts are diffed against what is already up, so a pan that keeps most of the
 * quilt only adds and removes at the edges and never refetches the middle. Pass an
 * empty list to clear.
 */
export function setRasterCharts(map: MaplibreMap, charts: ActiveRasterChart[]): void {
  const wanted = new Set(charts.map((c) => c.id));
  // Drop any of our layers that are not wanted any more. We read the live style
  // rather than trusting a remembered list, so a style rebuild cannot leave orphans.
  // Snapshot the layer list first: removing a layer mutates the live array, and
  // walking it as we go would step straight over the layer after each removal.
  for (const l of [...(map.getStyle()?.layers ?? [])]) {
    if (!isRasterChartLayer(l.id)) continue;
    if (l.id !== LEGACY_ID && wanted.has(l.id.slice(LYR_PREFIX.length))) continue;
    if (map.getLayer(l.id)) map.removeLayer(l.id);
  }
  for (const srcId of Object.keys(map.getStyle()?.sources ?? {})) {
    const ours = srcId === LEGACY_ID || srcId.startsWith(SRC_PREFIX);
    if (!ours) continue;
    if (srcId !== LEGACY_ID && wanted.has(srcId.slice(SRC_PREFIX.length))) continue;
    if (map.getSource(srcId)) map.removeSource(srcId);
  }
  if (!charts.length) return;

  const before = bottomBeforeId(map);
  for (const chart of charts) {
    const srcId = SRC_PREFIX + chart.id;
    const lyrId = LYR_PREFIX + chart.id;
    if (!map.getSource(srcId)) {
      // 256 px tiles so MapLibre fetches a deeper zoom on high-DPI screens (crisp on
      // phones); explicit min/max zoom so it overzooms the native top level cleanly
      // rather than requesting tiles that do not exist. `bounds` keeps each chart to
      // its own cut area, which is what lets neighbours sit side by side without one
      // chart's empty tiles painting over the chart next door.
      map.addSource(srcId, {
        type: 'raster',
        url: pmtilesUrl(chart.path, chart.source),
        tileSize: 256,
        bounds: chart.bounds,
        minzoom: chart.minZoom,
        maxzoom: chart.maxZoom,
        attribution: chart.attribution ?? 'Chart — not for navigation',
      });
    }
    if (!map.getLayer(lyrId)) {
      map.addLayer(
        { id: lyrId, type: 'raster', source: srcId, paint: { 'raster-fade-duration': 150 } },
        before,
      );
    }
  }
  // Re-seat every chart in order, coarsest first, so the detailed plans stay on top
  // of the wide chart however the set changed. Moving each one in turn to just under
  // the first content layer rebuilds the sequence from the bottom up.
  const anchor = bottomBeforeId(map);
  for (const chart of charts) {
    const lyrId = LYR_PREFIX + chart.id;
    if (map.getLayer(lyrId)) map.moveLayer(lyrId, anchor);
  }
}

/**
 * Show a single raster chart, or clear with null. Kept for the call sites that
 * genuinely mean one chart (a manual override) or that clear on a provider swap.
 */
export function setRasterChart(map: MaplibreMap, chart: ActiveRasterChart | null): void {
  setRasterCharts(map, chart ? [chart] : []);
}

/**
 * Hide or restore the vector cell layers (those whose id starts with `cell-`),
 * plus the authored TSS overlay (`tss-`), which belongs to the vector picture and
 * shouldn't double up over a raster that already draws the TSS itself.
 */
export function setCellLayersHidden(map: MaplibreMap, hidden: boolean): void {
  const layers = map.getStyle()?.layers ?? [];
  for (const l of layers) {
    if (l.id.startsWith('cell-') || l.id.startsWith('tss-')) {
      map.setLayoutProperty(l.id, 'visibility', hidden ? 'none' : 'visible');
    }
  }
}

/**
 * The opaque BASE fill layers of the vector picture: the depth-area shading and
 * the land / inland-water / built-up / building fills. These are what hide the
 * imagery beneath, so in the satellite-blend mode (vector chart over a photo) we
 * hide just these and keep every line, symbol, sounding and hazard drawing on
 * top. Matched by the layer-id suffix the style builder gives each one.
 */
const CELL_BASE_FILL_SUFFIXES = [
  '-depare-deep',
  '-depare-medium',
  '-depare-shallow',
  '-depare-drying',
  '-land-fill',
  '-river-fill',
  '-builtup-fill',
  '-building',
];

/**
 * Hide (for the satellite blend) or show the base fills only. NOTE: the restore
 * direction is normally driven by applyVisibility (which respects the category /
 * group toggles); this just forces the fills off when blending. Calling it with
 * `false` sets them plainly visible as a fallback.
 */
export function setCellBaseFillsHidden(map: MaplibreMap, hidden: boolean): void {
  const layers = map.getStyle()?.layers ?? [];
  for (const l of layers) {
    if (l.id.startsWith('cell-') && CELL_BASE_FILL_SUFFIXES.some((s) => l.id.endsWith(s))) {
      map.setLayoutProperty(l.id, 'visibility', hidden ? 'none' : 'visible');
    }
  }
}
